import { Component } from '@theme/component';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { buildSectionSelector, sectionRenderer } from '@theme/section-renderer';

class RecentlyViewedBlock extends Component {
  /**
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#load();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /** @type {() => void} */
  #handleStorageUpdated = () => {
    this.#load();
  };

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('recently-viewed:storage-updated', this.#handleStorageUpdated);
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('recently-viewed:storage-updated', this.#handleStorageUpdated);
    this.#intersectionObserver.disconnect();
  }

  /**
   * @param {Element | null | undefined} el
   * @returns {boolean}
   */
  #hasRenderableContent(el) {
    if (!el) return false;
    return Boolean(el.querySelector('.resource-list__item, .resource-list__slide, .product-card'));
  }

  async #load() {
    const { sectionId, productId, maxProducts } = this.dataset;

    if (!sectionId) {
      this.classList.add('hidden');
      return;
    }

    const limit = Number.parseInt(maxProducts || '8', 10);
    const cap = Number.isFinite(limit) && limit > 0 ? limit : 8;

    let ids = RecentlyViewed.getProducts().filter((id) => id !== String(productId));
    ids = ids.slice(0, cap);

    if (ids.length === 0) {
      this.classList.add('hidden');
      return;
    }

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', ids.map((/** @type {string} */ id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const sectionWrapper = parsed.getElementById(buildSectionSelector(sectionId));
      // Prefer the current instance id (stable for block-based PDP); section HTML may contain multiple recently-viewed blocks.
      const fresh =
        (this.id ? parsed.getElementById(this.id) : null) ??
        (this.id &&
        sectionWrapper &&
        typeof CSS !== 'undefined' &&
        typeof CSS.escape === 'function'
          ? sectionWrapper.querySelector(`#${CSS.escape(this.id)}`)
          : null) ??
        sectionWrapper?.querySelector('recently-viewed-section') ??
        parsed.querySelector('recently-viewed-section');

      if (this.#hasRenderableContent(fresh)) {
        this.innerHTML = /** @type {Element} */ (fresh).innerHTML;
        this.classList.remove('hidden');
        this.dataset.loaded = 'true';
        queueMicrotask(() => this.classList.remove('hidden'));
      } else {
        this.classList.add('hidden');
      }
    } catch (e) {
      console.error('Recently viewed block error:', e);
      this.classList.add('hidden');
    }
  }
}

if (!customElements.get('recently-viewed-section')) {
  customElements.define('recently-viewed-section', RecentlyViewedBlock);
}
